import dayjs from 'dayjs'
import {
  MAIN_STOCK_TAKE_REASON,
  PARTIAL_STOCK_TAKE_REASON,
} from './stockTakeCreateForm'
import { summarizeStockTakeProductProgress } from './stockTakePreferredPack'

const asText = value => String(value || '').trim()

/** List cards and detail H1: one date when from and to are the same calendar day. */
export const formatStockTakeDateRange = (fromPeriod, toPeriod) => {
  if (!fromPeriod || !toPeriod) return ''
  const start = dayjs(fromPeriod)
  const end = dayjs(toPeriod)
  if (!start.isValid() || !end.isValid()) return ''
  const startLabel = start.format('DD MMM YYYY')
  const endLabel = end.format('DD MMM YYYY')
  if (startLabel === endLabel) return startLabel
  return `${startLabel} - ${endLabel}`
}

export const isSupplierScopedStockTake = stockTake => {
  const ids = Array.isArray(stockTake?.supplierIds)
    ? stockTake.supplierIds.filter(Boolean)
    : []
  const titles = Array.isArray(stockTake?.supplierTitles)
    ? stockTake.supplierTitles.map(asText).filter(Boolean)
    : []
  return ids.length > 0 || titles.length > 0
}

/** Audit line only — do not persist over a stored Main reason. */
export const displayStockTakeReasonForCount = (stockTake = {}) => {
  const stored = asText(stockTake?.reasonForCount)
  if (isSupplierScopedStockTake(stockTake)) {
    if (!stored || stored === MAIN_STOCK_TAKE_REASON) {
      return PARTIAL_STOCK_TAKE_REASON
    }
  }
  return stored
}

export const formatStockTakeSupplierLabel = stockTake => {
  const titles = Array.isArray(stockTake?.supplierTitles)
    ? stockTake.supplierTitles.map(asText).filter(Boolean)
    : []
  if (titles.length) return [...new Set(titles)].join(', ')
  return asText(stockTake?.name)
}

const GENERIC_STOCK_TAKE_NAMES = new Set([
  'stock take',
  'stocktake',
  'main stock take',
  'stock take entry',
  'partial count',
  'partial',
])

export const isGenericStockTakeName = name => {
  const trimmed = asText(name)
  if (!trimmed) return true
  return GENERIC_STOCK_TAKE_NAMES.has(trimmed.toLowerCase())
}

export const namesMatchStockTakeLabel = (left, right) => {
  const a = asText(left).toLowerCase()
  const b = asText(right).toLowerCase()
  return Boolean(a) && a === b
}

export const formatPartialPrefixedHeading = label => {
  const text = asText(label)
  return text ? `Partial · ${text}` : ''
}

const nameMatchesCloneIdentity = (named, identity) => {
  if (!named || !identity) return false
  if (namesMatchStockTakeLabel(named, identity)) return true
  if (namesMatchStockTakeLabel(named, formatPartialPrefixedHeading(identity))) {
    return true
  }
  return named.toLowerCase() === `partial - ${identity.toLowerCase()}`
}

export const hasDistinctStockTakeName = stockTake => {
  const named = asText(stockTake?.name)
  if (isGenericStockTakeName(named)) return false
  const identity = stockTakeIdentityLabel(stockTake)
  if (identity && nameMatchesCloneIdentity(named, identity)) return false
  return true
}

const stockTakeIdentityLabel = stockTake => {
  const group =
    asText(stockTake?.partialGroupName) || asText(stockTake?.partialGroup?.name)
  const suppliers = formatStockTakeSupplierLabel({
    ...stockTake,
    name: '',
  })
  return suppliers || group
}

const productLineTitles = stockTake => {
  const lines = Array.isArray(stockTake?.products) ? stockTake.products : []
  return lines
    .map(line =>
      asText(
        line?.title ||
          line?.productTitle ||
          line?.name ||
          line?.product?.title ||
          line?.product?.name,
      ),
    )
    .filter(Boolean)
}

const identityTokens = label =>
  asText(label)
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(token => token.length > 2 && !/^\d+$/.test(token))

/** Group is “01 Cookies” but lines are bakery (no cookie titles). */
export const partialIdentityMismatchesProductLines = stockTake => {
  const label = stockTakeIdentityLabel(stockTake) || asText(stockTake?.name)
  const titles = productLineTitles(stockTake)
  const tokens = identityTokens(label)
  if (!titles.length || !tokens.length) return false
  return !titles.some(title => {
    const hay = title.toLowerCase()
    return tokens.some(token => hay.includes(token))
  })
}

/** Detail H1: real name, else supplier titles when Name is blank/generic. */
export const getStockTakePageHeading = (stockTake, fallback = 'Stock take') => {
  const named = asText(stockTake?.name)
  const suppliers = formatStockTakeSupplierLabel({
    ...stockTake,
    name: '',
  })
  const identity = stockTakeIdentityLabel(stockTake)
  const isPartial =
    String(stockTake?.countType || '').toUpperCase() === 'PARTIAL'
  const distinct =
    !isGenericStockTakeName(named) && !nameMatchesCloneIdentity(named, identity)

  if (distinct) return named

  const label = (!isGenericStockTakeName(named) ? named : '') || identity
  if (isPartial && label && partialIdentityMismatchesProductLines(stockTake)) {
    return formatPartialPrefixedHeading(label)
  }
  if (!isGenericStockTakeName(named)) return named
  if (suppliers) return suppliers
  if (isPartial && label) return formatPartialPrefixedHeading(label)
  return named || fallback
}

/** Extra supplier line only when it is not already the title. */
export const formatStockTakeListSupplierLine = stockTake => {
  const suppliers = formatStockTakeSupplierLabel({
    ...stockTake,
    name: '',
  })
  if (!suppliers) return ''
  const heading = getStockTakePageHeading(stockTake, '')
  if (namesMatchStockTakeLabel(suppliers, heading)) return ''
  if (heading === formatPartialPrefixedHeading(suppliers)) return ''
  return suppliers
}

const lookupGroup = (lookup, groupId) => {
  if (!lookup || !groupId) return null
  if (typeof lookup.get === 'function')
    return lookup.get(String(groupId)) || null
  return lookup[String(groupId)] || null
}

const snapshotProductCount = stockTake => {
  const lines = stockTake?.products
  if (Array.isArray(lines) && lines.length) {
    return summarizeStockTakeProductProgress(lines).total
  }
  const raw = Number(stockTake?.productCount)
  if (!Number.isFinite(raw)) return null
  return Math.max(0, Math.round(raw))
}

const groupIdOf = stockTake =>
  stockTake?.partialGroupId?._id ||
  stockTake?.partialGroupId ||
  stockTake?.partialGroup?._id ||
  ''

/**
 * Partial card chip: supplier counts show the supplier, not Unknown group.
 * Product count prefers snapshot lines from the list API.
 */
export const getPartialCardMeta = (stockTake, groupLookup) => {
  const countType = String(stockTake?.countType || '').toUpperCase()
  if (countType !== 'PARTIAL') return { name: '', productCount: 0 }

  const snapshot = snapshotProductCount(stockTake)
  const groupId = String(groupIdOf(stockTake) || '')
  const fromLookup = lookupGroup(groupLookup, groupId)
  const groupCount =
    Number(fromLookup?.productCount) ||
    Number(stockTake?.partialGroupProductCount) ||
    Number(stockTake?.partialGroup?.productCount) ||
    0
  const productCount =
    snapshot ?? (Number.isFinite(groupCount) ? groupCount : 0)

  if (isSupplierScopedStockTake(stockTake)) {
    return {
      name: formatStockTakeSupplierLabel(stockTake) || 'Supplier count',
      productCount,
      kind: 'supplier',
    }
  }

  const groupName =
    asText(stockTake?.partialGroupName) ||
    asText(stockTake?.partialGroup?.name) ||
    asText(fromLookup?.name)

  if (groupName) {
    return { name: groupName, productCount, kind: 'group' }
  }

  if (groupId) {
    const supplierName = formatStockTakeSupplierLabel(stockTake)
    if (supplierName) {
      return { name: supplierName, productCount, kind: 'supplier' }
    }
    return { name: 'Unknown group', productCount, kind: 'group' }
  }

  return { name: '', productCount: 0, kind: 'group' }
}

export const formatPartialCardLine = (stockTake, groupLookup) => {
  const { name, productCount, kind } = getPartialCardMeta(
    stockTake,
    groupLookup,
  )
  if (!name) return ''
  const countLabel =
    productCount === 1 ? '1 product' : `${productCount} products`
  if (
    kind === 'supplier' &&
    (namesMatchStockTakeLabel(name, getStockTakePageHeading(stockTake, '')) ||
      hasDistinctStockTakeName(stockTake))
  ) {
    return countLabel
  }
  return `${name} • ${countLabel}`
}

export const partialCardTooltip = (stockTake, groupLookup) => {
  const meta = getPartialCardMeta(stockTake, groupLookup)
  if (!meta.name) return ''
  const countLabel =
    meta.productCount === 1 ? '1 product' : `${meta.productCount} products`
  if (meta.kind === 'supplier') {
    return `${meta.name} · ${countLabel} on this count.`
  }
  return `This count belongs to the "${meta.name}" partial group (${countLabel}).`
}

const calendarDay = value => {
  if (value == null || value === '') return null
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null
}

const listHeadingCountType = stockTake =>
  String(stockTake?.countType || 'MAIN').toUpperCase() === 'PARTIAL'
    ? 'PARTIAL'
    : 'MAIN'

const listHeadingClashKey = stockTake =>
  `${listHeadingCountType(stockTake)}\0${getStockTakePageHeading(
    stockTake,
    '',
  )}`

/** Short date for a list title, matching Repeat’s “· 12 Oct” cue. */
export const stockTakeListHeadingDateCue = stockTake => {
  const fromIso = calendarDay(stockTake?.fromPeriod)
  const toIso = calendarDay(stockTake?.toPeriod) || fromIso
  if (!fromIso) return ''
  const from = dayjs(fromIso)
  const to = dayjs(toIso)
  if (fromIso === toIso) return from.format('D MMM')
  if (from.month() === to.month() && from.year() === to.year()) {
    return `${from.format('D')}–${to.format('D MMM')}`
  }
  return `${from.format('D MMM')}–${to.format('D MMM')}`
}

const stockTakeListHeadingCountedCue = stockTake => {
  const value = Number(stockTake?.totalStockValue)
  if (Number.isFinite(value) && value > 0) return 'Counted'
  const products = stockTake?.products
  if (
    Array.isArray(products) &&
    products.some(
      line =>
        line?.inStock != null &&
        line.inStock !== '' &&
        Number.isFinite(Number(line.inStock)),
    )
  ) {
    return 'Counted'
  }
  if (Number.isFinite(value) || Array.isArray(products)) return 'Not counted'
  return ''
}

/**
 * History card title. Same title + type as another card gets the date
 * (or Counted / Not counted) so two 01 Cookies rows are not identical.
 * Detail H1 stays getStockTakePageHeading.
 */
export const getStockTakeListHeading = (stockTake, peers = []) => {
  const base = getStockTakePageHeading(stockTake, '')
  if (!base) return base
  const key = listHeadingClashKey(stockTake)
  const clashes = (Array.isArray(peers) ? peers : []).filter(
    other => other && listHeadingClashKey(other) === key,
  )
  if (clashes.length < 2) return base
  const dates = new Set(
    clashes.map(item => stockTakeListHeadingDateCue(item)).filter(Boolean),
  )
  if (dates.size > 1) {
    const date = stockTakeListHeadingDateCue(stockTake)
    return date && !base.includes(date) ? `${base} · ${date}` : base
  }
  const counted = new Set(
    clashes.map(item => stockTakeListHeadingCountedCue(item)).filter(Boolean),
  )
  if (counted.size > 1) {
    const cue = stockTakeListHeadingCountedCue(stockTake)
    return cue && !base.includes(cue) ? `${base} · ${cue}` : base
  }
  const date = stockTakeListHeadingDateCue(stockTake)
  return date && !base.includes(date) ? `${base} · ${date}` : base
}

const stockTakeSupplierKey = stockTake => {
  const ids = Array.isArray(stockTake?.supplierIds)
    ? stockTake.supplierIds.map(id => String(id || '').trim()).filter(Boolean)
    : []
  if (ids.length) return `id:${[...ids].sort().join(',')}`
  const titles = formatStockTakeSupplierLabel({
    ...stockTake,
    name: '',
  }).toLowerCase()
  return titles ? `title:${titles}` : ''
}

const stockTakeListLooksCounted = stockTake => {
  const value = Number(stockTake?.totalStockValue)
  if (Number.isFinite(value) && Math.abs(value) > 0.0001) return true
  return stockTake?.linesCounted === true
}

const hasZeroProducts = stockTake => {
  const snapshot = snapshotProductCount(stockTake)
  if (snapshot === 0) return true
  if (snapshot == null && Array.isArray(stockTake?.products)) {
    return stockTake.products.length === 0
  }
  return false
}

const nameLooksLikeSupplierIdentity = stockTake => {
  if (!hasDistinctStockTakeName(stockTake)) return true
  const named = asText(stockTake?.name)
  const identity = stockTakeIdentityLabel(stockTake)
  return Boolean(named && identity && nameMatchesCloneIdentity(named, identity))
}

const isPartialOrSupplierClone = stockTake =>
  isSupplierScopedStockTake(stockTake) ||
  String(stockTake?.countType || '').toUpperCase() === 'PARTIAL'

export const isEmptySupplierCloneTake = (stockTake, allTakes = []) => {
  if (!stockTake) return false
  if (!isPartialOrSupplierClone(stockTake)) return false
  if (stockTakeListLooksCounted(stockTake)) return false
  if (hasZeroProducts(stockTake) && !hasDistinctStockTakeName(stockTake)) {
    return true
  }
  if (hasZeroProducts(stockTake) && nameLooksLikeSupplierIdentity(stockTake)) {
    return true
  }
  if (hasDistinctStockTakeName(stockTake)) return false
  const key = stockTakeSupplierKey(stockTake)
  const day =
    calendarDay(stockTake?.fromPeriod) || calendarDay(stockTake?.toPeriod)
  if (!key || !day) return false
  const id = String(stockTake?._id || '')
  return (Array.isArray(allTakes) ? allTakes : []).some(other => {
    if (!other || String(other._id || '') === id) return false
    if (stockTakeSupplierKey(other) !== key) return false
    const otherDay =
      calendarDay(other?.fromPeriod) || calendarDay(other?.toPeriod)
    return otherDay === day && stockTakeListLooksCounted(other)
  })
}

export const demoteZeroProductSupplierTakes = (stockTakes = []) => {
  const leading = []
  const demoted = []
  ;(Array.isArray(stockTakes) ? stockTakes : []).forEach(stockTake => {
    if (
      isSupplierScopedStockTake(stockTake) &&
      hasZeroProducts(stockTake) &&
      !stockTakeListLooksCounted(stockTake)
    ) {
      demoted.push(stockTake)
    } else {
      leading.push(stockTake)
    }
  })
  return [...leading, ...demoted]
}

export const filterEmptySupplierCloneTakes = (stockTakes = []) =>
  demoteZeroProductSupplierTakes(
    (Array.isArray(stockTakes) ? stockTakes : []).filter(
      stockTake => !isEmptySupplierCloneTake(stockTake, stockTakes),
    ),
  )

const isMainCount = stockTake =>
  String(stockTake?.countType || 'MAIN').toUpperCase() === 'MAIN'

export const stockTakeDateRangesOverlap = (left, right) => {
  const aFrom = calendarDay(left?.fromPeriod)
  const aTo = calendarDay(left?.toPeriod)
  const bFrom = calendarDay(right?.fromPeriod)
  const bTo = calendarDay(right?.toPeriod)
  if (!aFrom || !aTo || !bFrom || !bTo) return false
  return aFrom <= bTo && bFrom <= aTo
}

export const findOverlappingMainStockTake = (stockTake, allTakes = []) => {
  if (!isMainCount(stockTake)) return null
  const id = String(stockTake?._id || '')
  const peers = (Array.isArray(allTakes) ? allTakes : []).filter(other => {
    if (!other || String(other._id || '') === id) return false
    if (!isMainCount(other)) return false
    return stockTakeDateRangesOverlap(stockTake, other)
  })
  if (!peers.length) return null
  return [...peers].sort((a, b) => {
    const da = calendarDay(a.fromPeriod) || ''
    const db = calendarDay(b.fromPeriod) || ''
    return da.localeCompare(db) || String(a._id).localeCompare(String(b._id))
  })[0]
}

const sameStockTakeDateRange = (left, right) => {
  const aFrom = calendarDay(left?.fromPeriod)
  const aTo = calendarDay(left?.toPeriod)
  const bFrom = calendarDay(right?.fromPeriod)
  const bTo = calendarDay(right?.toPeriod)
  return Boolean(aFrom && aTo && aFrom === bFrom && aTo === bTo)
}

const isUnlockedStockTake = stockTake => stockTake?.isLocked !== true

/**
 * History cue only. Create still validates overlap on save.
 * Locked-vs-locked and same-range twins (“overlaps itself”) stay quiet.
 * Cards are not merged or deleted.
 */
export const formatStockTakeOverlapCue = (stockTake, allTakes = []) => {
  const peer = findOverlappingMainStockTake(stockTake, allTakes)
  if (!peer) return ''
  if (!isUnlockedStockTake(stockTake) && !isUnlockedStockTake(peer)) {
    return ''
  }
  const heading = getStockTakePageHeading(peer, '')
  const selfHeading = getStockTakePageHeading(stockTake, '')
  if (
    sameStockTakeDateRange(stockTake, peer) &&
    namesMatchStockTakeLabel(heading, selfHeading)
  ) {
    return ''
  }
  const other =
    heading && !isGenericStockTakeName(heading)
      ? heading
      : formatStockTakeDateRange(peer.fromPeriod, peer.toPeriod)
  return other ? `overlaps ${other}` : ''
}
